import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Plus, Package, Clock, CheckCircle, Truck } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static style objects defined once to avoid re-creating on every render.
const AXIS_STYLE_MONO = { fontFamily: 'IBM Plex Mono' };
const TOOLTIP_STYLE = { border: '1px solid #E4E4E7', fontFamily: 'IBM Plex Sans' };

export const DashboardPage = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({
    pending: 0,
    in_progress: 0,
    completed: 0,
    delivered: 0
  });
  const [monthlyData, setMonthlyData] = useState([]);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      const ordersData = response.data;
      setOrders(ordersData.slice(0, 5));

      const statsCounts = {
        pending: ordersData.filter(o => o.status === 'pending').length,
        in_progress: ordersData.filter(o => o.status === 'in_progress').length,
        completed: ordersData.filter(o => o.status === 'completed').length,
        delivered: ordersData.filter(o => o.status === 'delivered').length
      };
      setStats(statsCounts);
    } catch (error) {
      logger.error('Error fetching orders:', error);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data.slice(0, 5));
    } catch (error) {
      logger.error('Error fetching notifications:', error);
    }
  }, []);

  const fetchMonthlyStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/statistics/monthly`);
      setMonthlyData(response.data.slice(-6));
    } catch (error) {
      logger.error('Error fetching monthly stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchNotifications();
    if (user?.role === 'admin') {
      fetchMonthlyStats();
    }
  }, [user, fetchOrders, fetchNotifications, fetchMonthlyStats]);

  const markAsRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`);
      fetchNotifications();
    } catch (error) {
      logger.error('Error marking notification as read:', error);
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Dashboard</h1>
            <p className="text-base text-muted-foreground mt-2">Bienvenido, {user?.name}</p>
          </div>
          {(user?.role === 'reception' || user?.role === 'admin') && (
            <Link to="/orders/new">
              <Button data-testid="create-order-button" className="bg-primary hover:bg-primary/90">
                <Plus size={20} className="mr-2" weight="bold" />
                Nueva Orden
              </Button>
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-grid">
          <div className="p-6 border border-border bg-background">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Pendientes</p>
                <p className="text-4xl font-black tracking-tighter mt-2">{stats.pending}</p>
              </div>
              <Clock size={48} weight="fill" className="text-muted-foreground" />
            </div>
          </div>

          <div className="p-6 border border-border bg-background">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">En Proceso</p>
                <p className="text-4xl font-black tracking-tighter mt-2">{stats.in_progress}</p>
              </div>
              <Package size={48} weight="fill" className="text-primary" />
            </div>
          </div>

          <div className="p-6 border border-border bg-background">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Completadas</p>
                <p className="text-4xl font-black tracking-tighter mt-2">{stats.completed}</p>
              </div>
              <CheckCircle size={48} weight="fill" className="text-foreground" />
            </div>
          </div>

          <div className="p-6 border border-border bg-background">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Entregadas</p>
                <p className="text-4xl font-black tracking-tighter mt-2">{stats.delivered}</p>
              </div>
              <Truck size={48} weight="fill" className="text-foreground" />
            </div>
          </div>
        </div>

        {user?.role === 'admin' && monthlyData.length > 0 && (
          <div className="p-8 border border-border bg-background">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">Órdenes por Mes</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="month" stroke="#09090B" style={AXIS_STYLE_MONO} />
                <YAxis stroke="#09090B" style={AXIS_STYLE_MONO} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" fill="#E11D48" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-border bg-background">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Órdenes Recientes</h2>
            </div>
            <div className="divide-y divide-border">
              {orders.length > 0 ? (
                orders.map((order) => (
                  <Link
                    key={order.id}
                    to={`/orders/${order.id}`}
                    className="block p-6 hover:bg-secondary/50"
                    data-testid={`order-${order.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold font-mono">{order.order_number}</p>
                        <p className="text-sm text-muted-foreground mt-1">{order.client_name}</p>
                      </div>
                      <div className="text-right">
                        <span
                          className={`inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider border ${
                            order.status === 'pending'
                              ? 'border-muted bg-muted text-foreground'
                              : order.status === 'in_progress'
                              ? 'border-primary bg-primary/10 text-primary'
                              : order.status === 'completed'
                              ? 'border-foreground bg-foreground/10 text-foreground'
                              : 'border-foreground bg-foreground text-background'
                          }`}
                        >
                          {order.status === 'pending' && 'Pendiente'}
                          {order.status === 'in_progress' && 'En Proceso'}
                          {order.status === 'completed' && 'Completada'}
                          {order.status === 'delivered' && 'Entregada'}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="p-6 text-center text-muted-foreground">No hay órdenes</div>
              )}
            </div>
          </div>

          <div className="border border-border bg-background">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">Notificaciones</h2>
            </div>
            <div className="divide-y divide-border max-h-96 overflow-y-auto">
              {notifications.length > 0 ? (
                notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`p-6 ${
                      notif.read ? 'bg-background' : 'bg-secondary/50'
                    }`}
                    data-testid={`notification-${notif.id}`}
                  >
                    <p className="text-sm">{notif.message}</p>
                    {!notif.read && (
                      <button
                        onClick={() => markAsRead(notif.id)}
                        className="text-xs text-primary hover:underline mt-2"
                        data-testid={`mark-read-${notif.id}`}
                      >
                        Marcar como leída
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-muted-foreground">No hay notificaciones</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
